// ─────────────────────────────────────────────────────────────────────────────
// useNodeCoreDownload — the behaviour behind the two Node Core download buttons.
//
// Both screens that offer them (the Add Node wizard's Ready step and the per-row
// Download Package modal) use this hook and render their own markup, so the
// request rules live in one place while each screen keeps its own styling.
//
// The two artifacts are deliberately independent: the binary comes from S3 and
// fails on IAM/config problems, the config comes from our own DB and fails on
// node state (409 while the node is online). One being unavailable must never
// block the other, so they have separate loading and error state rather than a
// shared "downloading" flag.
//
// ── USER ACTIVATION: why the binary path is shaped the way it is ─────────────
// Browsers only allow a click to navigate or start a download while the click's
// TRANSIENT USER ACTIVATION is still live. An `await` in the click handler can
// consume it, after which Chrome drops the navigation SILENTLY — no download, no
// console error, a button that simply appears dead.
//
// So the binary never awaits before navigating, in either path:
//   fast path — reuse the URL prefetched when the screen opened (it is valid for
//               ~30 min, and the screen fetches it on mount anyway to show the
//               file size), so the navigation is fully synchronous with the click
//   slow path — no usable URL yet: open the tab FIRST, synchronously inside the
//               gesture, and point it at the URL once the request resolves
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchNodeCoreBinaryUrl,
  fetchNodeCoreConfig,
} from "../../../services/nodesService";
import { downloadBinary, downloadConfig } from "../components/nodeCoreDownload";

/**
 * Refuse to reuse a prefetched URL with less than this left. A URL that expires
 * mid-download is not the risk (only the initial request is signed) — the risk is
 * handing the browser one that S3 has already stopped honouring, which renders an
 * "AccessDenied / Request has expired" XML page instead of a file.
 */
const MIN_REMAINING_MS = 60_000;

/** Milliseconds of life left in a binary-url response, or 0 if unknown. */
function remainingMs(meta) {
  if (!meta?.expires_at) return 0;
  const t = new Date(meta.expires_at).getTime();
  return Number.isNaN(t) ? 0 : t - Date.now();
}

/**
 * @param {string} nodeId
 * @param {{ platform?: "linux" }} [opts]
 */
export default function useNodeCoreDownload(nodeId, { platform = "linux" } = {}) {
  const [binaryLoading, setBinaryLoading] = useState(false);
  const [binaryError,   setBinaryError]   = useState(null);
  const [binaryMeta,    setBinaryMeta]    = useState(null);

  const [configLoading, setConfigLoading] = useState(false);
  const [configError,   setConfigError]   = useState(null);
  const [configResult,  setConfigResult]  = useState(null);

  // The same descriptor as `binaryMeta`, reachable synchronously. State is read
  // through a closure that may be a render behind, and the fast path above has to
  // decide DURING the click — a stale read there would send it down the slow path
  // unnecessarily, or worse, reuse an expired URL.
  const binaryMetaRef = useRef(null);

  // These modals can be closed mid-request. Writing state after unmount is a
  // React warning and, worse, would keep a config payload (which holds a live
  // enrollment token) referenced by a dead component.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const storeMeta = useCallback((res) => {
    binaryMetaRef.current = res;
    if (alive.current) setBinaryMeta(res);
  }, []);

  /**
   * Fetch the binary's descriptor WITHOUT downloading it.
   *
   * Two jobs: it lets the screen show the real size and platform before the
   * operator commits, and it primes the URL that makes the click synchronous.
   * Safe to call on mount — the endpoint is a read with no side effects.
   */
  const loadBinaryMeta = useCallback(async () => {
    if (!nodeId) return;
    setBinaryLoading(true);
    setBinaryError(null);
    try {
      storeMeta(await fetchNodeCoreBinaryUrl(nodeId, { platform }));
    } catch (err) {
      if (alive.current) setBinaryError(err.message);
    } finally {
      if (alive.current) setBinaryLoading(false);
    }
  }, [nodeId, platform, storeMeta]);

  /**
   * Download the binary. MUST be called directly from a click handler — see the
   * user-activation note in the header.
   */
  const getBinary = useCallback(() => {
    if (!nodeId) return;
    setBinaryError(null);

    // ── Fast path: a prefetched URL with comfortable life left. No await before
    // the navigation, so the click's activation is intact.
    const cached = binaryMetaRef.current;
    if (cached?.binary_url && remainingMs(cached) > MIN_REMAINING_MS) {
      downloadBinary(cached.binary_url);
      // Re-prime for the next click; failure here is invisible on purpose, since
      // the download the operator asked for has already started.
      fetchNodeCoreBinaryUrl(nodeId, { platform }).then(storeMeta).catch(() => {});
      return;
    }

    // ── Slow path: nothing usable cached, so the URL has to be fetched and the
    // activation is likely gone by the time it lands.
    //
    // Deliberately NOT window.open() here. Pre-opening a tab inside the gesture
    // does survive the await, but it makes the download a popup, and a
    // popup-blocker extension swallows it silently — observed in the field, with
    // a stubbed window object and the URL filed away under `pp_pending_ntf`.
    // Trading a silent failure for a different silent failure is not a fix.
    //
    // So: attempt the anchor anyway (it works whenever activation survived), and
    // expose the URL either way so the screen can offer a plain link the operator
    // can click themselves. A visible fallback beats an invisible failure.
    setBinaryLoading(true);
    fetchNodeCoreBinaryUrl(nodeId, { platform })
      .then((res) => {
        storeMeta(res);
        downloadBinary(res.binary_url);
      })
      .catch((err) => { if (alive.current) setBinaryError(err.message); })
      .finally(() => { if (alive.current) setBinaryLoading(false); });
  }, [nodeId, platform, storeMeta]);

  /**
   * Generate (or re-serve) the node's config and save it as node-core.json.
   *
   * This is a WRITE: it can mint a token, extend the node's enrollment deadline
   * and restore a node the server had expired. It is idempotent within the
   * token's lifetime — a node with a live token gets the same one back — so
   * clicking twice is safe and does not invalidate an already-saved file.
   *
   * Unaffected by the activation problem above: the file is built in-page as a
   * same-origin blob, and those are not subject to the popup/navigation rules.
   */
  const getConfig = useCallback(async () => {
    if (!nodeId) return;
    setConfigLoading(true);
    setConfigError(null);
    try {
      const res = await fetchNodeCoreConfig(nodeId);
      if (!alive.current) return;
      setConfigResult(res);
      downloadConfig(res.config, res.filename);
    } catch (err) {
      if (!alive.current) return;
      setConfigError(err.message);
    } finally {
      if (alive.current) setConfigLoading(false);
    }
  }, [nodeId]);

  return {
    // binary
    loadBinaryMeta,
    getBinary,
    binaryLoading,
    binaryError,
    binaryMeta,
    // config
    getConfig,
    configLoading,
    configError,
    configResult,
  };
}
